import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { getBaseRequestData } from '../../../utils/evergentUtils';
import { ConfigService } from '../../config.service';
import {
  GetProductsRequestMessage,
  ProductsRequestMessageModel,
} from './model/products-request-message.model';
import { GetProductsResponseMessage } from './model/products-response-message.model';
import { getApiUrl } from 'src/app/shared/utils/apiUtils';
import { LanguageService } from 'src/app/language.service';
import { AccountProfileService } from '../account/account-profile.service';
import { AuthService } from '../../auth/auth.service';
import { GeoLocationService } from '../../geolocation.service';
import { getPreferredLanguageKeys } from 'src/app/shared/utils/deviceUtils';
import { LocalStorageService } from '../../storage/local-storage.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';

/**
 * Constants for default values and device types
 */
const DEFAULTS = {
  DMA_ID: '001',
  RETURN_APP_CHANNELS: 'T',
  RETURN_PROMOTIONS: 'T',
  RETURN_ATTRIBUTES: 'T',
  RETURN_LOCALE_DISPLAY_NAME: 'T',
} as const;

const DEVICE_TYPES = {
  WEBOS: 'Web0S',
  LG: 'Lg',
  SAMSUNG: 'Samsung',
} as const;

/**
 * Service responsible for handling product-related operations.
 * Manages fetching products and their associated information.
 *
 * @class ProductsService
 */
@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  /** UMS API URL */
  private umsUrl: string;

  /**
   * Initializes the service with required dependencies
   * @param http - HTTP client for making API requests
   * @param configService - Service for accessing application configuration
   */
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private readonly accountProfileService: AccountProfileService,
    private readonly authService: AuthService,
    private readonly geoLocationService: GeoLocationService,
    private readonly localStorage: LocalStorageService
  ) {
    this.umsUrl = this.configService.applicationConfig.UmsURL;
  }

  getProducts(
    dmaId: string = DEFAULTS.DMA_ID,
    returnAppChannels: string = DEFAULTS.RETURN_APP_CHANNELS,
    returnPromotions: string = DEFAULTS.RETURN_PROMOTIONS
  ): Observable<GetProductsResponseMessage> {
    const url = getApiUrl(this.umsUrl, 'GET_PRODUCTS');
    const requestMessage = this.createProductsRequestMessage(
      dmaId,
      returnAppChannels,
      returnPromotions
    );

    const headers = this.getHeaders();

    return this.http
      .post<GetProductsResponseMessage>(url, requestMessage, {
        headers,
      })
      .pipe(catchError(this.handleError));
  }

  private getHeaders(): HttpHeaders | null {
    const token = this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
    if (!token || token === 'undefined') {
      return null;
    }
    return new HttpHeaders({
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  /**
   * Creates a products request message
   * @param dmaId - DMA ID for filtering products
   * @param returnAppChannels - Flag to return app channels
   * @param returnPromotions - Flag to return promotions
   * @returns GetProductsRequestMessage - The formatted request message
   */
  private createProductsRequestMessage(
    dmaId: string,
    returnAppChannels: string,
    returnPromotions: string
  ): GetProductsRequestMessage {
    const requestMessage = new GetProductsRequestMessage();
    const requestContent: ProductsRequestMessageModel = {
      ...new ProductsRequestMessageModel(),
      ...getBaseRequestData(this.configService.applicationConfig),
      dmaID: dmaId,
      returnAppChannels,
      returnPromotions,
      salesChannel: this.getDeviceType(),
      returnAttributes: DEFAULTS.RETURN_ATTRIBUTES,
      returnLocaleDisplayName: DEFAULTS.RETURN_LOCALE_DISPLAY_NAME,
      offerType: this.getOfferType(),
    };
    requestMessage.GetProductsRequestMessage = requestContent;
    return requestMessage;
  }

  public getOfferType() {
    const discountedRegions =
      this.configService.applicationConfig.discountedRegions;

    const isDisRegContainsTn =
      discountedRegions?.toLowerCase().includes('tn') == true;
    const isDisRegContainsNonTN =
      discountedRegions?.toLowerCase().includes('non-tn') == true;

    const neverSubscribed =
      this.accountProfileService?.account?.GetAccountProfileResponseMessage
        ?.neverSubscribed == true;
    const loggedIn = this.authService.isLoggedIn();

    const isTamilNadu =
      this.geoLocationService.getRegion()?.toLowerCase().includes('tn') == true;
    const mainProfilePSL =
      this.accountProfileService.mainProfile?.primarySpeakingLanguage;
    const isOnlyTamilPreferredLanguage = mainProfilePSL
      ? mainProfilePSL.toLowerCase() === 'tamil'
      : getPreferredLanguageKeys().toLowerCase() === 'ta';

    let offerType = 'Unified';
    if (
      this.geoLocationService.isFromIndia() &&
      (!loggedIn || neverSubscribed) &&
      isOnlyTamilPreferredLanguage &&
      ((isTamilNadu && isDisRegContainsTn) ||
        (!isTamilNadu && isDisRegContainsNonTN))
    ) {
      offerType = 'UnifiedTN';
    }
    return offerType;
  }

  /**
   * Determines the device type based on user agent
   * @returns string - The device type (Lg or Samsung)
   */
  public getDeviceType(): string {
    return navigator.userAgent.toString().includes(DEVICE_TYPES.WEBOS)
      ? DEVICE_TYPES.LG
      : DEVICE_TYPES.SAMSUNG;
  }

  /**
   * Handles HTTP errors
   * @param error - The HTTP error response
   * @returns Observable<never> - Error observable
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Backend returned code ${error.status}, body was: ${error.error}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
